import { useEffect, useState, useRef } from "react";
import {
  Space,
  Table,
  Tooltip,
  Pagination,
  Popconfirm,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Alert,
} from "antd";
import type { TableProps } from "antd";
import BaseService from "../../service/base.service";
import PatientModel from "../../models/patient";
import PatientDetails from "../../models/patientDetails";
import PaginationResponse from "../../models/paginationResponse";
import { Avatar, Tag } from "antd";
import { UserOutlined, CalendarOutlined } from "@ant-design/icons";

import {
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  CloseOutlined,
  SaveOutlined,
  PlusOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import notify from "../../components/common/notificationHelper";
import { useIsMobile } from "../../hooks/useIsMobile";

interface FormValues {
  id: string;
  firstName: string;
  lastName: string;
  birthDate: dayjs.Dayjs | null;
  bloodType: string;
  phoneNumber: string;
  email: string;
  address: string;
}

const PAGE_SIZE = 10;

const PatientsPage = () => {
  const isMobile = useIsMobile();
  const [listPatients, setListPatients] = useState<PatientModel[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const { Option } = Select;
  const horizontalPadding = isMobile ? 12 : 24;
  const contentRef = useRef<HTMLDivElement | null>(null);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableScrollY, setTableScrollY] = useState<number>(400);

  useEffect(() => {
    const calculateTableScrollHeight = () => {
      if (tableContainerRef.current) {
        const containerHeight = tableContainerRef.current.clientHeight;
        const offset = isMobile ? 72 : 100;
        setTableScrollY(Math.max(containerHeight - offset, 200));
      }
    };
    calculateTableScrollHeight();
    window.addEventListener("resize", calculateTableScrollHeight);
    return () => window.removeEventListener("resize", calculateTableScrollHeight);
  }, [isMobile]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchText(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    handleGetAllPatients(currentPage, searchText);
  }, [currentPage, searchText]);

  useEffect(() => {
    if (isModalOpen) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (contentRef.current) {
            contentRef.current.scrollTop = 0;
          } else {
            const modalBody = document.querySelector(".ant-modal-body") as HTMLElement | null;
            if (modalBody) modalBody.scrollTop = 0;
          }
        }, 0);
      });
    }
  }, [isModalOpen]);

  const handleGetAllPatients = async (page: number, search: string) => {
    setLoading(true);
    try {
      let url = `/patients?page=${page - 1}&size=${PAGE_SIZE}`;
      if (search && search.trim() !== "") {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }
      const rp = await BaseService.get<PaginationResponse<PatientModel>>(url, null);
      if (rp.Status) {
        const paginationResponse = rp.Data || [];
        const patientsList = paginationResponse.data.map(
          (p: any) => new PatientModel(p.id, p.firstName, p.lastName, p.age, p.gender, p.bloodType, p.phoneNumber, p.email, p.nextAppointment, p.lastExam)
        );
        setListPatients(patientsList);
        setTotalElements(paginationResponse.totalElements);
        setCurrentPage(page);
      } else {
        console.log("Messages: " + rp.Messages);
        console.log("Exception: " + rp.Exception);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePatient = (patient: PatientModel) => {
    BaseService.delete("/patients/", patient.id).then((rp) => {
      if (rp.Status) {
        notify({
          title: "Patient deleted successfully",
          description: `${patient.firstName} ${patient.lastName} has been deleted.`,
          variant: "info",
        });
        handleGetAllPatients(currentPage, searchText);
      } else {
        notify({
          title: "Error deleting patient",
          description: `Failed to delete ${patient.firstName} ${patient.lastName}.`,
          variant: "error",
        });
      }
    });
  };

  const handleAddAndEditPatient = (values: FormValues) => {
    const payload = {
      ...values,
      birthDate: values.birthDate ? values.birthDate.format("DD.MM.YYYY") : null,
    };

    const request = isEdit
      ? BaseService.update("/patients/", payload.id, payload)
      : BaseService.create("/patients", payload);

    request.then((rp) => {
      if (rp.Status) {
        notify({
          title: isEdit ? "Patient updated successfully" : "Patient added successfully",
          description: `${payload.firstName} ${payload.lastName} has been ${isEdit ? "updated" : "saved"
            }.`,
          variant: "success",
        });
        handleGetAllPatients(currentPage, searchText);
      } else {
        notify({
          title: "Error saving patient",
          description: `Failed to save ${payload.firstName} ${payload.lastName}.`,
          variant: "error",
        });
      }
    });

    setIsModalOpen(false);
  };

  const handleCancel = () => setIsModalOpen(false);

  const handleAdd = () => {
    setIsEdit(false);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handlePageChange = (page: number) => setCurrentPage(page);

  const handleEdit = async (patient: PatientModel) => {
    try {
      const rp = await BaseService.get<PatientDetails>("/patients/", patient.id);
      if (rp.Status) {
        setIsEdit(true);
        const data = rp.Data;
        form.setFieldsValue({
          ...data,
          birthDate: dayjs(data.birthDate, "DD.MM.YYYY"),
        });
        setIsModalOpen(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const renderActions = (record: PatientModel) => (
    <Space size="middle">
      <Tooltip title="Edit patient">
        <EditOutlined
          style={{ cursor: "pointer", color: "#1677ff" }}
          onClick={() => handleEdit(record)}
        />
      </Tooltip>

      <Tooltip title="View examinations">
        <FileTextOutlined
          style={{ fontSize: 18, color: "#52c41a", cursor: "pointer" }}
          onClick={() => navigate(`/examination/${record.id}`)}
        />
      </Tooltip>

      <Tooltip title="Delete patient">
        <Popconfirm
          onConfirm={() => handleDeletePatient(record)}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ danger: true }}
          title={
            <div>
              Are you sure you want to delete{" "}
              <strong>
                {record.firstName} {record.lastName}
              </strong>
              ?
              <div style={{ color: "red", marginTop: 4 }}>
                ⚠️ This will delete all related examinations.
              </div>
            </div>
          }
        >
          <DeleteOutlined style={{ color: "#ff4d4f", cursor: "pointer" }} />
        </Popconfirm>
      </Tooltip>
    </Space>
  );

  const columns: TableProps<PatientModel>["columns"] = isMobile
    ? [
        {
          title: "Patient",
          key: "patient",
          render: (_: unknown, record: PatientModel) => (
            <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              <Avatar
                size={36}
                style={{
                  backgroundColor: "#1677ff22",
                  color: "#1677ff",
                  fontWeight: 600,
                  marginRight: 10,
                  flexShrink: 0,
                }}
                icon={!record.firstName ? <UserOutlined /> : undefined}
              >
                {record.firstName && record.lastName
                  ? `${record.firstName[0]}${record.lastName[0]}`
                  : null}
              </Avatar>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 600,
                    color: "#1f2937",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {record.firstName} {record.lastName}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  {record.age ? `${record.age} yrs` : "—"}
                  {record.bloodType ? ` · ${record.bloodType}` : ""}
                </div>
                {record.phoneNumber && (
                  <div style={{ fontSize: 12, color: "#6b7280" }}>{record.phoneNumber}</div>
                )}
              </div>
            </div>
          ),
        },
        {
          title: "",
          key: "actions",
          width: 96,
          align: "right",
          render: (_: unknown, record: PatientModel) => renderActions(record),
        },
      ]
    : [
        {
          title: "Patient",
          key: "patient",
          render: (_: unknown, record: PatientModel) => (
            <div style={{ display: "flex", alignItems: "center" }}>
              <Avatar
                size={40}
                style={{
                  backgroundColor: "#1677ff22",
                  color: "#1677ff",
                  fontWeight: 600,
                  marginRight: 12,
                }}
                icon={!record.firstName ? <UserOutlined /> : undefined}
              >
                {record.firstName && record.lastName
                  ? `${record.firstName[0]}${record.lastName[0]}`
                  : null}
              </Avatar>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontWeight: 600, color: "#1f2937" }}>
                  {record.firstName} {record.lastName}
                </span>
                <span style={{ fontSize: 12, color: "#6b7280" }}>
                  {record.age ? `${record.age} yrs` : "—"}
                </span>
              </div>
            </div>
          ),
        },
        {
          title: "Blood Type",
          key: "medical",
          align: "center",
          render: (_: unknown, record: PatientModel) => (
            <div>
              {record.bloodType && (
                <Tag color="blue" style={{ marginTop: 4 }}>
                  {record.bloodType}
                </Tag>
              )}
            </div>
          ),
        },
        {
          title: "Last Examination",
          key: "lastExam",
          render: (_: unknown, record: PatientModel) =>
            record.lastExam && record.lastExam.type && record.lastExam.date ? (
              <div>
                <span style={{ fontWeight: 500, color: "#1f2937" }}>
                  {record.lastExam.type}
                </span>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {record.lastExam.date}
                </div>
              </div>
            ) : (
              <span style={{ color: "#9ca3af", fontStyle: "italic" }}>No exams yet</span>
            ),
        },
        {
          title: "Next Appointment",
          key: "nextAppointment",
          render: (_: unknown, record: PatientModel) =>
            record.nextAppointment ? (
              <Tag color="green" icon={<CalendarOutlined />}>
                {record.nextAppointment}
              </Tag>
            ) : (
              <span style={{ color: "#9ca3af", fontStyle: "italic" }}>
                No appointments
              </span>
            ),
        },
        {
          title: "Contact",
          key: "contact",
          render: (_: unknown, record: PatientModel) => (
            <div style={{ lineHeight: 1.4 }}>
              {record.phoneNumber && (
                <div style={{ color: "#1f2937", fontSize: 13 }}>
                  {record.phoneNumber}
                </div>
              )}
              {record.email && (
                <div style={{ color: "#6b7280", fontSize: 12 }}>{record.email}</div>
              )}
            </div>
          ),
        },
        {
          title: "Actions",
          key: "actions",
          align: "center",
          render: (_: unknown, record: PatientModel) => renderActions(record),
        },
      ];



  return (
    <>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          height: "100%",
          padding: isMobile ? "8px 0 16px" : "16px 16px 24px",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <div style={{ marginBottom: isMobile ? 12 : 24, flexShrink: 0 }}>
          <Alert
            message={
              isMobile
                ? "Manage patients and their examination reports."
                : "Manage patient records and access their medical examination reports from here."
            }
            type="info"
            showIcon={!isMobile}
            style={{
              background: "#f0f7ff",
              borderColor: "#d0e4ff",
              color: "#2c5282",
              borderRadius: 8,
              padding: isMobile ? "8px 12px" : "10px 14px",
              fontSize: isMobile ? 13 : 14,
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            justifyContent: "space-between",
            gap: isMobile ? 10 : 12,
            background: "#fff",
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: isMobile ? "10px 12px" : "12px 16px",
            marginBottom: isMobile ? 12 : 16,
            boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
            flexShrink: 0,
          }}
        >
          <Input
            placeholder="Search by first or last name"
            allowClear
            style={{ width: isMobile ? "100%" : 280 }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />

          <Button
            type="primary"
            onClick={handleAdd}
            icon={<PlusOutlined />}
            block={isMobile}
            style={{
              borderRadius: 6,
              padding: "6px 18px",
              fontWeight: 500,
            }}
          >
            Add Patient
          </Button>
        </div>

        <div
          ref={tableContainerRef}
          style={{
            flex: 1,
            minHeight: 0,
            background: "#fff",
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
            padding: isMobile ? 10 : 16,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Table
            columns={columns}
            dataSource={listPatients}
            pagination={false}
            scroll={{ y: tableScrollY, x: isMobile ? undefined : true }}
            sticky
            bordered={false}
            loading={loading}
            locale={{ emptyText: "No patients found" }}
            rowKey="id"
            size={isMobile ? "small" : "middle"}
          />
        </div>

        <div
          style={{
            marginTop: isMobile ? 12 : 20,
            display: "flex",
            justifyContent: "center",
            paddingBottom: isMobile ? 8 : 24,
            flexShrink: 0,
          }}
        >
          <Pagination
            current={currentPage}
            pageSize={PAGE_SIZE}
            total={totalElements}
            onChange={handlePageChange}
            showSizeChanger={false}
            size={isMobile ? "small" : "default"}
            simple={isMobile}
          />
        </div>
      </div>

      {/* Modal */}
      <Modal
        title={
          <div
            style={{
              textAlign: "center",
              fontWeight: 600,
              fontSize: isMobile ? "1.2rem" : "1.5rem",
              color: "#1f1f1f",
              borderBottom: "1px solid #f0f0f0",
              paddingBottom: 12,
            }}
          >
            {isEdit ? (
              <>
                <EditOutlined style={{ color: "#1677ff", marginRight: 8 }} />
                Edit Patient
              </>
            ) : (
              <>
                <UserAddOutlined style={{ color: "#52c41a", marginRight: 8 }} />
                Add Patient
              </>
            )}
            <div
              style={{
                fontSize: isMobile ? 13 : 14,
                fontWeight: 400,
                color: "#6b7280",
                marginTop: 4,
              }}
            >
              {isEdit
                ? "Update patient information below"
                : "Fill in the details to add a new patient"}
            </div>
          </div>
        }
        centered
        open={isModalOpen}
        footer={null}
        width={isMobile ? "calc(100vw - 24px)" : "60vw"}
        confirmLoading={loading}
        onCancel={handleCancel}
        styles={{
          body: {
            maxHeight: isMobile ? "75vh" : "80vh",
            overflowY: "auto",
            position: "relative",
            padding: isMobile ? "12px 12px 0" : undefined,
          },
        }}
      >
        <div
          ref={contentRef}
          style={{
            maxHeight: isMobile ? "55vh" : "65vh",
            overflowY: "auto",
            paddingRight: 8,
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Form<FormValues>
            form={form}
            name="validateOnly"
            layout="vertical"
            autoComplete="off"
            onFinish={handleAddAndEditPatient}
            style={{ padding: `16px ${horizontalPadding}px 0` }}
          >
            {isEdit && (
              <Form.Item name="id" label="Patient ID" rules={[{ required: true }]}>
                <Input disabled />
              </Form.Item>
            )}
            <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="birthDate" label="Birth Date" rules={[{ required: true }]}>
              <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="bloodType" label="Blood Type">
              <Select placeholder="Select blood type">
                <Option value="A+">A+</Option>
                <Option value="A-">A−</Option>
                <Option value="B+">B+</Option>
                <Option value="B-">B−</Option>
                <Option value="AB+">AB+</Option>
                <Option value="AB-">AB−</Option>
                <Option value="O+">O+</Option>
                <Option value="O-">O−</Option>
              </Select>
            </Form.Item>
            <Form.Item name="phoneNumber" label="Phone Number">
              <Input />
            </Form.Item>
            <Form.Item name="email" label="Email">
              <Input />
            </Form.Item>
            <Form.Item name="address" label="Address">
              <Input />
            </Form.Item>
          </Form>
        </div>

        <div
          style={{
            position: "sticky",
            bottom: 0,
            background: "#fff",
            padding: isMobile ? "12px 8px" : "16px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            borderTop: "1px solid #f0f0f0",
            boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.04)",
            zIndex: 10,
          }}
        >
          <Button onClick={handleCancel} danger icon={<CloseOutlined />}>
            Cancel
          </Button>

          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => form.submit()}
            loading={loading}
          >
            {isEdit ? "Update" : "Save"}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default PatientsPage;
